import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_VIDEOS } from "../mock-data";

gsap.registerPlugin(ScrollTrigger);

// Lightweight canvas starfield — no Three.js needed
function StarfieldCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const stars: { x: number; y: number; r: number; speed: number; opacity: number }[] = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 120; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.3 + 0.05,
        opacity: Math.random() * 0.7 + 0.3,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((s) => {
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 220, 160, ${s.opacity})`;
        ctx.fill();
        s.opacity += Math.sin(Date.now() * s.speed * 0.01) * 0.01;
        s.opacity = Math.max(0.1, Math.min(1, s.opacity));
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

export function DarkRomanceLayout({ formSlot, program, title, dateTime, location, description, slug }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  const dateStr = dateTime
    ? new Date(dateTime).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : null;
  const timeStr = dateTime
    ? new Date(dateTime).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.8, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // Hero title glow fade-in
      gsap.from(".dr-title", {
        opacity: 0,
        scale: 0.95,
        duration: 2,
        ease: "power2.out",
        delay: 0.5,
      });
      gsap.from(".dr-subtitle", {
        opacity: 0,
        duration: 1.5,
        delay: 1.5,
        ease: "power1.out",
      });

      // Story lines slide from left
      gsap.from(".dr-story-line", {
        opacity: 0,
        x: -50,
        stagger: 0.1,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: { trigger: ".dr-story-section", start: "top 75%", toggleActions: "play reverse play reverse" },
      });

      // Date numbers dramatic scale
      gsap.from(".dr-date-block", {
        opacity: 0,
        scale: 0.7,
        duration: 1.2,
        ease: "back.out(1.7)",
        scrollTrigger: { trigger: ".dr-date-section", start: "top 70%", toggleActions: "play reverse play reverse" },
      });

      // Form reveal
      gsap.from(".dr-form-wrapper", {
        opacity: 0,
        y: 50,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: { trigger: ".dr-form-wrapper", start: "top 80%", toggleActions: "play reverse play reverse" },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  return (
    <main className="bg-[#060408] text-white overflow-x-hidden">
      {/* ── Hero: Ambient video + starfield ──────────────────────────── */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/*
          AI VIDEO BRIEF — Dark Romance ambient:
          "Macro close-up of candles flickering at night, golden sparks falling,
          ambient dark moody atmosphere, slow motion. 10-15s seamless loop."

          Placeholder paylaşımlı /demo/cinematic-hero.mp4 (içerik tematik değil,
          sadece ambient loop yerleşimini gösterir — opacity 0.18 ile çok zayıf görünür).
        */}
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-[0.18]"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={(e) => { (e.currentTarget as HTMLVideoElement).style.display = "none"; }}
        >
          <source src={DEMO_VIDEOS.heroLoopAv1} type='video/mp4; codecs="av01.0.05M.08"' />
          <source src={DEMO_VIDEOS.heroLoop} type='video/mp4; codecs="avc1.42E01E"' />
        </video>
        {/* Bokeh radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(180,120,80,0.12)_0%,_transparent_70%)]" />
        {/* Bottom fade */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#060408]" />

        {/* Starfield canvas */}
        <StarfieldCanvas />

        {/* Content */}
        <div className="relative z-10 text-center px-6">
          <p className="dr-subtitle text-[10px] tracking-[0.6em] uppercase text-amber-300/40 mb-8">
            Düğün Davetiyesi
          </p>
          <h1
            className="dr-title font-serif text-5xl sm:text-7xl md:text-8xl font-light tracking-wide leading-tight"
            style={{
              color: "#e8c99a",
              textShadow: "0 0 40px rgba(220,170,100,0.3), 0 0 80px rgba(220,170,100,0.1)",
            }}
          >
            {title}
          </h1>
          {dateStr && (
            <p className="dr-subtitle mt-8 text-xs tracking-[0.4em] uppercase text-amber-300/40">
              {dateStr}
            </p>
          )}
        </div>
      </section>

      {/* ── Story ─────────────────────────────────────────────────────── */}
      <section className="dr-story-section min-h-screen flex items-center py-24 px-6 bg-[#060408]">
        <div className="max-w-2xl mx-auto">
          <div className="w-8 h-px bg-amber-400/30 mb-10" />
          <p className="text-[10px] tracking-[0.5em] uppercase text-amber-400/30 mb-8">Hikayemiz</p>
          {(description ?? "İki yıl önce bir yaz akşamı tanıştık. Sizi özel günümüze davet etmekten onur duyuyoruz.")
            .split(". ")
            .filter(Boolean)
            .map((s, i) => (
              <p key={i} className="dr-story-line text-xl sm:text-2xl font-serif font-light text-white/75 leading-relaxed mb-4">
                {s.trim()}.
              </p>
            ))}
        </div>
      </section>

      {/* ── Date ─────────────────────────────────────────────────────── */}
      <section className="dr-date-section relative min-h-[80vh] flex items-center justify-center py-24 overflow-hidden bg-[#0a0608]">
        {/* Night sky background */}
        <div className="absolute inset-0 opacity-30">
          <StarfieldCanvas />
        </div>
        <div className="dr-date-block relative z-10 text-center">
          <p className="text-[10px] tracking-[0.5em] uppercase text-amber-400/30 mb-10">Tarih & Mekan</p>
          <p
            className="font-serif text-[clamp(3rem,10vw,7rem)] font-light leading-none"
            style={{ color: "#c9a96e", textShadow: "0 0 30px rgba(201,169,110,0.25)" }}
          >
            {dateStr ?? "--"}
          </p>
          {timeStr && (
            <p className="mt-4 text-2xl tracking-widest text-amber-300/50">{timeStr}</p>
          )}
          {location && (
            <p className="mt-8 text-base text-white/40 max-w-sm mx-auto tracking-wide">{location}</p>
          )}
        </div>
      </section>

      {/* ── Form ─────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center py-24 px-6 bg-[#060408]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(150,80,50,0.08)_0%,_transparent_60%)]" />
        <div className="dr-form-wrapper relative z-10 w-full max-w-md">
          <p className="text-center text-[10px] tracking-[0.5em] uppercase text-amber-400/30 mb-4">Katılım</p>
          <h2
            className="text-center text-3xl font-serif font-light mb-10"
            style={{ color: "#c9a96e" }}
          >
            Bize Katılın
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
